import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, snakeCase } from 'lodash-es';
import { useInView } from 'react-intersection-observer';
import ActivityIcon from 'components/ActivityIcon';
import I18n from 'utils/I18n';
import styles from './styles.scss';
import ActivityEntryBody from '../shared/ActivityEntryBody';
import MapVisPrompt from '../shared/MapVisPrompt';
import MaharajAchievementCelebration from '../shared/MaharajAchievementCelebration';
import PhotosAndMapImage from '../shared/PhotosAndMapImage';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import useFeedIntersectionObserver from '../../hooks/useFeedIntersectionObserver';
import KudosAndComments from '../shared/KudosAndComments';
import {
  segAndBestEffortAchievementsProps,
  kudosAndCommentsProps,
  feedTypeProps,
  mapProps,
  pageProps,
  photoListProps,
  powerAndSegmentGoalAchievementsProps,
  statsProps,
  viewingAthleteProps,
  cursorDataProps,
  indexProps,
  entityAthleteProps,
  timeAndLocationProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../shared/FeedEntityContext';
import KudosAndCommentsContext from '../../../shared/KudosCommentsAndAchievements/KudosAndCommentsContext';
import {
  initKudosAndCommentsState,
  kudosAndCommentsReducer
} from '../../../shared/KudosCommentsAndAchievements/kudosAndComments.reducer';
import {
  feedEntityInit,
  feedEntityReducer
} from '../shared/feedEntity.reducer';
import { SET_AUTOPLAY_VIDEO_IN_VIEW } from '../shared/entity-actions';
import { rootMargin } from '../../../Feed/utils';
import { FEED_ENTRY_TYPES } from '../../constants';
import CommentDeleteAlertDialog from '../../../shared/KudosCommentsAndAchievements/CommentDeleteAlertDialog';
import { buildInitialFeedState } from '../shared/feedEntity.init-state';
import { buildInitialKudosAndCommentsState } from '../../../shared/KudosCommentsAndAchievements/kudosAndComments.initial-state';
import HeaderWithOwnerMetadata from '../shared/HeaderWithOwnerMetadata/HeaderWithOwnerMetadata';
import VisibilityMessage from '../shared/VisibilityMessage';
import HiddenStatIndicator from '../shared/HiddenStatIndicator';
import Card from '../shared/media/Card';
import EntryBody from '../shared/media/EntryBody';
import Media from '../shared/media/Media';
import MediaLeft from '../shared/media/MediaLeft';
import MediaBody from '../shared/media/MediaBody';
import EntryMedia from '../shared/media/EntryMedia';
import EntryFooter from '../shared/media/EntryFooter';

const Activity = ({
  index,
  entity,
  activity,
  cursorData,
  page,
  feedType,
  viewingAthlete,
  clubId,
  athleteProfileId,
  isAutoplayEnabled,
  clubMentionsEnabled,
  extraEntityActions,
  extraTrackingProperties
}) => {
  const {
    avatarUrl,
    athleteName,
    athleteId,
    memberType
  } = activity.athlete;

  const {
    activityName,
    id: activityId,
    flagged,
    ownedByCurrentAthlete,
    segAndBestEffortAchievements,
    powerAndSegmentGoalAchievements,
    type: sportType,
    stats,
    description,
    descriptionMentionedAthleteIds,
    descriptionMentionsClubIds,
    mapVisPrompt,
    maharajAchievement,
    isVirtual,
    isCommute,
    workoutType,
    privacyTagKey,
    shareable,
    twitterUrl,
    isBoosted,
    visibility,
    hiddenStatIndicatorString,
    mapAndPhotos,
    embedDropdownEnabled
  } = activity;

  const analyticProps = {
    page,
    entity,
    entityId: activityId,
    athleteProfileId,
    feedType,
    index,
    viewingAthleteId: viewingAthlete.id,
    clubId,
    properties: {
      mentioned_athletes: descriptionMentionedAthleteIds || [],
      mentioned_clubs: descriptionMentionsClubIds || [],
      ...extraTrackingProperties
    }
  };

  const { inView, intersectionObserverRef } = useFeedIntersectionObserver({
    index,
    isParentFeedEntry: true
  });

  const { trackAnalytics } = useFeedAnalytics({
    ...analyticProps,
    inView
  });

  const [kudosAndCommentsEntityState, dispatch] = useReducer(
    kudosAndCommentsReducer,
    buildInitialKudosAndCommentsState({
      trackAnalytics,
      comments: { [activityId]: activity.kudosAndComments.comments },
      entity: snakeCase(entity),
      entityId: activityId
    }),
    initKudosAndCommentsState
  );

  const [feedEntityState, feedDispatch] = useReducer(
    feedEntityReducer,
    buildInitialFeedState({
      trackAnalytics,
      entity: snakeCase(entity),
      entityId: activityId,
      isAutoplayEnabled
    }),
    feedEntityInit
  );

  const kudosAndCommentsProviderState = {
    kudosAndCommentsEntityState,
    dispatch,
    analyticProps
  };

  const feedProviderState = {
    feedEntityState,
    feedDispatch,
    analyticProps
  };

  const translatedSportType = I18n.t(
    `strava.activities.types_v2.${sportType.toLowerCase()}`
  );

  const buildEntryBody = () => {
    const elements = [];
    // activity icon
    elements.push(
      <MediaLeft key="activity_icon">
        <div className={`${styles.entryIcon} `}>
          <ActivityIcon
            type={sportType}
            size="small"
            className={styles.activityIcon}
            title={translatedSportType}
          />
        </div>
      </MediaLeft>
    );
    // activity title, description, stats and achievements
    elements.push(
      <MediaBody key="activity_entry_body">
        <ActivityEntryBody
          stats={stats}
          description={description}
          flagged={flagged}
          activityName={activityName}
          activityId={activityId}
          ownedByCurrentAthlete={ownedByCurrentAthlete}
          segAndBestEffortAchievements={segAndBestEffortAchievements}
          powerAndSegmentGoalAchievements={powerAndSegmentGoalAchievements}
        />
      </MediaBody>
    );
    return elements;
  };

  const entityOwner = {
    displayImage: avatarUrl,
    name: athleteName,
    memberType,
    profileUrl: `/athletes/${athleteId}`,
    type: 'athlete'
  };

  const showVisibilityMessage =
    ownedByCurrentAthlete && visibility !== 'everyone';

  const showHiddenStatIndicator =
    ownedByCurrentAthlete && hiddenStatIndicatorString !== null;

  const [inViewAutoPlayRef, inViewAutoPlay] = useInView({
    threshold: 0,
    // 11 here is the gap between feed entries -1px to make sure that two feed
    // entries never overlap and preventing two videos playing at the same time.
    rootMargin: rootMargin(11)
  });

  const { autoPlayVideos } = feedEntityState;
  const autoPlayVideo = autoPlayVideos[activityId];
  const playPromiseRef = useRef();

  useEffect(() => {
    if (isAutoplayEnabled && autoPlayVideo) {
      if (inViewAutoPlay) {
        playPromiseRef.current = autoPlayVideo.play();
        feedDispatch({
          type: SET_AUTOPLAY_VIDEO_IN_VIEW,
          payload: { autoPlayVideoInView: true }
        });
      } else if (playPromiseRef.current !== undefined) {
        playPromiseRef.current.then(() => {
          autoPlayVideo.pause();
          feedDispatch({
            type: SET_AUTOPLAY_VIDEO_IN_VIEW,
            payload: { autoPlayVideoInView: false }
          });
        });
      }
    }
  }, [isAutoplayEnabled, autoPlayVideo, inViewAutoPlay, playPromiseRef]);

  const observerRes = useCallback(
    (node) => {
      if (isAutoplayEnabled) {
        inViewAutoPlayRef(node);
      }
      intersectionObserverRef(node);
    },
    [intersectionObserverRef, inViewAutoPlayRef, isAutoplayEnabled]
  );

  const buildEntryFooter = () => (
    <EntryFooter>
      {showHiddenStatIndicator && (
        <HiddenStatIndicator
          hiddenStatIndicatorString={hiddenStatIndicatorString}
          showVisibilityMessage={showVisibilityMessage}
        />
      )}
      {showVisibilityMessage && <VisibilityMessage visibility={visibility} />}
      <KudosAndComments
        kudosAndComments={activity.kudosAndComments}
        entity={snakeCase(entity)}
        entityId={activityId}
        ownedByCurrentAthlete={ownedByCurrentAthlete}
        viewingAthlete={viewingAthlete}
        entityOwner={entityOwner}
        clubMentionsEnabled={clubMentionsEnabled}
        entityTitle={activityName}
        openCommentsModalOnIconClick={false}
        segAndBestEffortAchievements={segAndBestEffortAchievements}
        showHairlineOnTop={
          (isEmpty(mapAndPhotos) && isEmpty(maharajAchievement)) ||
          showVisibilityMessage ||
          showHiddenStatIndicator
        }
      />
    </EntryFooter>
  );

  return (
    <KudosAndCommentsContext.Provider value={kudosAndCommentsProviderState}>
      <FeedEntityContext.Provider value={feedProviderState}>
        <Card
          key={index}
          index={index}
          cursorData={cursorData}
          ref={observerRes}
        >
          {/* Entry Header - avatar, location, social actions */}
          <HeaderWithOwnerMetadata
            timeAndLocation={activity.timeAndLocation}
            entityOwner={entityOwner}
            entityId={activityId}
            isBoosted={isBoosted || false}
            shareable={shareable || false}
            twitterUrl={twitterUrl}
            embedDropdownEnabled={embedDropdownEnabled}
            entityParentType={FEED_ENTRY_TYPES.activity}
            entityParentId={parseInt(athleteId, 10)}
            extraEntityActions={extraEntityActions}
          />
          {/* Entry Body =  activity title, description, stats and achievements  */}
          <EntryBody>
            <Media>{buildEntryBody()}</Media>
          </EntryBody>

          {/* Map Visibility feature education row prompt */}
          {/* Prioritize achievements over the map visibility feature education row prompt and hide
           the prompt when there is an achievement to show */}
          {!isEmpty(mapVisPrompt) && isEmpty(maharajAchievement) && (
            <EntryMedia>
              <MapVisPrompt
                hideAnywhereSetting={mapVisPrompt.hideAnywhereSetting}
                paths={mapVisPrompt.paths}
                entityId={activityId}
              />
            </EntryMedia>
          )}

          {/* Maharaj achievements */}
          {!isEmpty(maharajAchievement) && (
            <EntryMedia>
              <MaharajAchievementCelebration
                image={maharajAchievement.image}
                image_caption={maharajAchievement.image_caption}
                subtitle={maharajAchievement.subtitle}
                text={maharajAchievement.text}
                url={maharajAchievement.url}
                type={maharajAchievement.type}
                analytics={maharajAchievement.analytics}
                activityOwnerId={athleteId}
              />
            </EntryMedia>
          )}

          {/* Map and Photos */}
          {!isEmpty(mapAndPhotos) && (
            <EntryMedia>
              <PhotosAndMapImage
                activityId={activityId}
                activityMap={mapAndPhotos.activityMap}
                entryType={entity.toLowerCase()}
                isRoutable={mapAndPhotos.isRoutable}
                photoList={mapAndPhotos.photoList}
                feedName={page}
                isVirtual={isVirtual}
                isCommute={isCommute}
                workoutType={workoutType}
                privacyTagKey={privacyTagKey}
              />
            </EntryMedia>
          )}

          {/* Entry Footer - Visibility Message , Kudos & Comments */}
          {buildEntryFooter()}
          <CommentDeleteAlertDialog />
        </Card>
      </FeedEntityContext.Provider>
    </KudosAndCommentsContext.Provider>
  );
};

Activity.propTypes = {
  // feed entry types could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['Activity']).isRequired,
  // range of number 0...19 representing rows returned by the server per feed load
  index: indexProps.isRequired,
  clubMentionsEnabled: PropTypes.bool.isRequired,
  activity: PropTypes.shape({
    activityName: PropTypes.string,
    id: PropTypes.string,
    flagged: PropTypes.bool,
    athlete_id: PropTypes.number,
    ownedByCurrentAthlete: PropTypes.bool,
    segAndBestEffortAchievements: segAndBestEffortAchievementsProps,
    powerAndSegmentGoalAchievements: powerAndSegmentGoalAchievementsProps,
    type: PropTypes.string,
    stats: statsProps,
    description: PropTypes.string,
    descriptionMentionedAthleteIds: PropTypes.arrayOf(PropTypes.string),
    descriptionMentionsClubIds: PropTypes.arrayOf(PropTypes.string),
    mapVisPrompt: PropTypes.shape({
      hideAnywhereSetting: PropTypes.string,
      paths: PropTypes.shape({
        promptImpression: PropTypes.string,
        hideAnywhere: PropTypes.string
      })
    }),
    maharajAchievement: PropTypes.shape({
      image: PropTypes.string,
      image_caption: PropTypes.string,
      subtitle: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string,
      type: PropTypes.string,
      analytics: PropTypes.shape({})
    }),
    isVirtual: PropTypes.bool.isRequired,
    isCommute: PropTypes.bool.isRequired,
    workoutType: PropTypes.string,
    privacyTagKey: PropTypes.string,
    shareable: PropTypes.bool.isRequired,
    twitterUrl: PropTypes.string.isRequired,
    isBoosted: PropTypes.bool,
    visibility: PropTypes.string.isRequired,
    hiddenStatIndicatorString: PropTypes.string,
    athlete: entityAthleteProps.isRequired,
    mapAndPhotos: PropTypes.shape({
      activityMap: mapProps,
      isRoutable: PropTypes.bool,
      photoList: photoListProps
    }).isRequired,
    timeAndLocation: timeAndLocationProps.isRequired,
    kudosAndComments: kudosAndCommentsProps.isRequired,
    embedDropdownEnabled: PropTypes.bool.isRequired
  }).isRequired,
  cursorData: cursorDataProps.isRequired,
  page: pageProps.isRequired,
  feedType: feedTypeProps.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  isAutoplayEnabled: PropTypes.bool,
  extraEntityActions: PropTypes.shape({}),
  extraTrackingProperties: PropTypes.shape({})
};

Activity.defaultProps = {
  isAutoplayEnabled: true,
  extraEntityActions: undefined,
  extraTrackingProperties: undefined
};

export default Activity;
